import React from "react";
import { Link } from "react-router-dom";
import { imgUrl, money } from "../../lib/api";
import VideoEmbed from "./VideoEmbed";

export default function HomeSection({ section }) {
  const t = section.type;
  const d = section.data || {};

  if (t === "carousel" || t === "banner_strip") {
    const banners = d.banners || [];
    if (!banners.length) return null;
    return (
      <section className="px-6 md:px-12 lg:px-24 py-10" data-testid={`section-${section.slug}`}>
        <div className={`grid gap-3 ${t === "carousel" ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          {banners.map((b) => (
            <a key={b.id} href={b.link_url || "#"} className="relative block overflow-hidden group aspect-[16/9] md:aspect-[16/7]">
              <picture>
                {b.mobile_image_url && (
                  <source media="(max-width: 767px)" srcSet={imgUrl(b.mobile_image_url)} />
                )}
                <img
                  src={imgUrl(b.image_url)}
                  alt={b.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <div className="overline text-white/80">{b.subtitle}</div>
                <div className="font-display text-2xl">{b.title}</div>
                {b.cta_label && <div className="mt-1 text-xs uppercase tracking-widest underline">{b.cta_label}</div>}
              </div>
            </a>
          ))}
        </div>
      </section>
    );
  }

  if (t === "category_grid") {
    const cats = d.categories || [];
    if (!cats.length) return null;
    return (
      <section className="px-6 md:px-12 lg:px-24 py-12" data-testid={`section-${section.slug}`}>
        <div className="overline mb-3">Featured</div>
        <h2 className="font-display text-3xl lg:text-4xl mb-8">{section.title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cats.map((c) => (
            <Link key={c.id} to={`/shop/${c.slug}`} className="relative overflow-hidden aspect-[3/4] group">
              {c.image_url && <img src={imgUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <div className="font-display text-xl">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    );
  }

  if (t === "product_grid") {
    const products = d.products || [];
    if (!products.length) return null;
    return (
      <section className="px-6 md:px-12 lg:px-24 py-12" data-testid={`section-${section.slug}`}>
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="overline mb-2">Curated</div>
            <h2 className="font-display text-3xl lg:text-4xl">{section.title}</h2>
          </div>
          <Link to="/shop" className="text-sm uppercase tracking-widest underline">Shop all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {products.map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} className="block group">
              <div className="aspect-[3/4] overflow-hidden bg-[hsl(var(--kb-surface-alt))] mb-2">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="w-full h-full object-cover kb-product-img" />}
              </div>
              <div className="text-sm line-clamp-1">{p.title}</div>
              <div className="text-sm font-medium">{money(p.price)}</div>
            </Link>
          ))}
        </div>
      </section>
    );
  }

  if (t === "brand_story") {
    return (
      <section className="grid md:grid-cols-2 min-h-[60vh]" data-testid={`section-${section.slug}`}>
        <div className="relative aspect-square md:aspect-auto">
          {d.image_url && <img src={imgUrl(d.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        </div>
        <div className="flex flex-col justify-center p-10 md:p-20 bg-[hsl(var(--kb-primary))] text-white">
          <div className="overline text-white/70 mb-4">{section.title}</div>
          <h2 className="font-display text-3xl lg:text-5xl mb-6 leading-tight">{d.heading || section.title}</h2>
          {d.body && <p className="text-white/80 leading-relaxed mb-6">{d.body}</p>}
          {d.cta_url && d.cta_label && (
            <Link to={d.cta_url} className="bg-white text-[hsl(var(--kb-primary))] px-8 py-4 uppercase text-sm tracking-wider font-medium inline-block w-max">
              {d.cta_label}
            </Link>
          )}
        </div>
      </section>
    );
  }

  if (t === "announcement_bar") {
    const banners = d.banners || [];
    if (!banners.length) return null;
    return (
      <section
        className="bg-[hsl(var(--kb-primary))] text-white text-[11px] md:text-xs tracking-[0.2em] uppercase"
        data-testid={`section-${section.slug}`}
      >
        <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-center gap-x-8 gap-y-1">
          {banners.map((b) => (
            <a key={b.id} href={b.link_url || "#"} className="hover:underline flex items-center gap-2">
              {b.title}
              {b.cta_label && <span className="opacity-70">· {b.cta_label} →</span>}
            </a>
          ))}
        </div>
      </section>
    );
  }

  if (t === "coupon_banner") {
    return (
      <section
        className="px-6 md:px-12 lg:px-24 py-10"
        data-testid={`section-${section.slug}`}
      >
        <div
          className="border-2 border-dashed border-[hsl(var(--kb-primary))] p-8 md:p-12 text-center"
          style={d.background ? { background: d.background } : undefined}
        >
          <div className="overline mb-3 text-[hsl(var(--kb-primary))]">Limited Offer</div>
          <h2 className="font-display text-3xl lg:text-4xl mb-3">{d.heading || section.title}</h2>
          {d.body && <p className="text-[hsl(var(--kb-muted))] mb-4 max-w-xl mx-auto">{d.body}</p>}
          {d.code && (
            <div className="inline-flex items-center gap-3 border border-[hsl(var(--kb-primary))] px-5 py-2 bg-white mb-5">
              <span className="text-xs uppercase tracking-widest text-[hsl(var(--kb-muted))]">Code</span>
              <span className="font-mono font-medium text-[hsl(var(--kb-primary))]">{d.code}</span>
              {d.coupon?.description && (
                <span className="text-xs text-[hsl(var(--kb-muted))]">· {d.coupon.description}</span>
              )}
            </div>
          )}
          <div>
            <Link
              to={d.cta_url || "/shop"}
              className="kb-btn-primary inline-block"
              data-testid={`section-${section.slug}-cta`}
            >
              {d.cta_label || "Shop Now"}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (t === "seo_block") {
    const cols = d.columns || [];
    return (
      <section
        className="px-6 md:px-12 lg:px-24 py-12 border-t border-[hsl(var(--kb-border))]"
        data-testid={`section-${section.slug}`}
      >
        {(d.heading || section.title) && (
          <h2 className="font-display text-2xl md:text-3xl mb-4">{d.heading || section.title}</h2>
        )}
        {d.body && <p className="text-[hsl(var(--kb-muted))] max-w-3xl leading-relaxed mb-8">{d.body}</p>}
        {cols.length > 0 && (
          <div className="grid md:grid-cols-3 gap-8">
            {cols.map((c, i) => (
              <div key={i}>
                {c.heading && <div className="overline mb-2">{c.heading}</div>}
                {c.body && <p className="text-sm text-[hsl(var(--kb-muted))] leading-relaxed">{c.body}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  if (t === "trending_products" || t === "featured_collection") {
    const products = d.products || [];
    if (!products.length) return null;
    return (
      <section className="px-6 md:px-12 lg:px-24 py-12" data-testid={`section-${section.slug}`}>
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="overline mb-2">{t === "trending_products" ? "Trending" : "Featured collection"}</div>
            <h2 className="font-display text-3xl lg:text-4xl">{section.title}</h2>
          </div>
          <Link to="/shop" className="text-sm uppercase tracking-widest underline">Shop all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {products.map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} className="block group">
              <div className="aspect-[3/4] overflow-hidden bg-[hsl(var(--kb-surface-alt))] mb-2">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="w-full h-full object-cover kb-product-img" />}
              </div>
              <div className="text-sm line-clamp-1">{p.title}</div>
              <div className="text-sm font-medium">{money(p.price)}</div>
            </Link>
          ))}
        </div>
      </section>
    );
  }

  if (t === "reels_rail") {
    const reels = d.reels || [];
    if (!reels.length) return null;
    return (
      <section
        className="px-6 md:px-12 lg:px-24 py-12"
        data-testid={`section-reels-${section.slug || section.title || "rail"}`}
      >
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            {section.title && <div className="overline mb-2">{section.title}</div>}
            {d.heading && <h2 className="font-display text-3xl lg:text-4xl">{d.heading}</h2>}
            {d.subtitle && <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-xl">{d.subtitle}</p>}
          </div>
          <Link to={d.cta_url || "/reels"} className="text-sm uppercase tracking-widest underline">
            {d.cta_label || "See all reels →"}
          </Link>
        </div>
        <div className="flex gap-4 overflow-x-auto snap-x pb-4 -mx-2 px-2 kb-scroll-hide">
          {reels.map((c) => (
            <div key={c.id} className="snap-start shrink-0 w-64 md:w-72">
              <Link to={`/reels?content=${c.id}`} className="block">
                <VideoEmbed content={c} aspect="9x16" testid={`campaign-reel-${c.id}`} />
              </Link>
              <div className="text-sm font-medium mt-2 line-clamp-2">{c.title}</div>
              {Array.isArray(c.linked_products) && c.linked_products.length > 0 && (
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                  Shop {c.linked_products.length} piece{c.linked_products.length > 1 ? "s" : ""}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    );
  }

  return null;
}
